import * as React from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { v4 as guid } from "uuid";
import { SessionScheduleAction } from "../../../misc/api/apiTypes";
import { ReorderActions } from "../DragDropHelpers";
import { useSessionScheduleActionContext } from "../SessionScheduleActionContext";
import { GetBinStyle } from "../VisitSchedule";

interface DiarySessionFlowProps {
    questionnaires;
    alertRules;
    complianceRules;
    transmissionMethods;
}

export enum ActionType {
    'questionnaire',
    'alert',
    'spirometry',
    'compliance',
    'transmission',
    'unsupervisedspirometry',
    'supervisedspirometry',
    'spirometryinfo'
}

interface ActionItemProps  {
    action: SessionScheduleAction;
    index: number;
}

const grid = 6;

export const getItemStyle = (isDragging, draggableStyle): React.CSSProperties => {
    return {
        userSelect: "none",
        padding: grid * 2,
        margin: `0 0 ${grid}px 0`,

        background: isDragging ? "lightgreen" : "#fff",

        ...draggableStyle
    };
};

const getListStyle = isDraggingOver => ({
    background: isDraggingOver ? "lightblue" : "#f1f2f2",
    margin: `${grid}px 0 ${grid}px 0`,
    padding: grid,
    width: "100%"
});

export const DiarySessionFlow = ({ questionnaires, alertRules, complianceRules, transmissionMethods }: DiarySessionFlowProps) => {
    const { actions, setActions } = useSessionScheduleActionContext();

    const changeActionValue = (id: string, value) => {
        setActions(
            actions.map(sa => {
                if (sa.Id === id) {
                    return {
                        ...sa,
                        ActionValue: value,
                    }
                } else {
                    return sa;
                }
            })
        );
    }

    const spirometryItem = (action: SessionScheduleAction, index) => {
        return (
            <Draggable
                key={`SessionFlowSpirometry${action.Id}`}
                draggableId={action.Id}
                index={index}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <div className="row" {...provided.dragHandleProps}>
                            <div className="col-md-2">
                                <i className={`fas fa-wind fa-2x`}></i>&nbsp;Spirometry
                            </div>
                            <div className="col-md-10">
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>);
    };

    const unsupervisedSpirometryItem = (action: SessionScheduleAction, index) => {
        return (
            <Draggable
                key={`SessionFlowUnsupervisedSpirometry${action.Id}`}
                draggableId={action.Id}
                index={index}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <div className="row" {...provided.dragHandleProps}>
                            <div className="col-md-3">
                                <i className={`fas fa-user-unlock fa-2x`}></i>&nbsp;Unsupervised Spirometry
                            </div>
                            <div className="col-md-9">
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>);
    };

    const supervisedSpirometryItem = (action: SessionScheduleAction, index) => {
        return (
            <Draggable
                key={`SessionFlowUnsupervisedSpirometry${action.Id}`}
                draggableId={action.Id}
                index={index}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <div className="row" {...provided.dragHandleProps}>
                            <div className="col-md-3">
                                <i className={`fas fa-user-headset fa-2x`}></i>&nbsp;Supervised Spirometry
                            </div>
                            <div className="col-md-9">
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>);
    };

    const spirometryInfoItem = (action: SessionScheduleAction, index) => {
        return (
            <Draggable
                key={`SessionFlowSpirometryInfo${action.Id}`}
                draggableId={action.Id}
                index={index}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <div className="row" {...provided.dragHandleProps}>
                            <div className="col-md-3">
                                <i className={`fas fa-info-circle fa-2x`}></i>&nbsp;Spirometry Info
                            </div>
                            <div className="col-md-9">
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>);
    };

    const item = (action: SessionScheduleAction, index, typeName: string, icon: string, listItems: { Key: string; Name: string }[]) => {
        return (
            <Draggable
                key={`SessionFlowAction${action.Id}`}
                draggableId={action.Id}
                index={index}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <div className="row" {...provided.dragHandleProps}>
                            <div className="col-md-3">
                                <i className={`fas ${icon} fa-2x`}></i>&nbsp;{typeName}
                            </div>
                            <div className="col-md-9">
                                <select className="form-control" value={action.ActionValue} onChange={(e) => changeActionValue(action.Id, e.target.value)}>
                                    <option key="Blank" value={null}></option>
                                    {listItems.map(q => (
                                        <option key={`${q.Key}`} value={q.Key}>
                                            {q.Name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>
        );
    }

    const ActionItems = ({ action, index }: ActionItemProps) => {
        switch (action.ActionType) {
            case ActionType.questionnaire: {
                return item(action, index, "Questionnaire", "fa-poll-h", questionnaires);
            } case ActionType.alert: {
                return item(action, index, "Alert", "fa-sensor-alert", alertRules);
            } case ActionType.spirometry: {
                return spirometryItem(action, index);
            } case ActionType.unsupervisedspirometry: {
                return unsupervisedSpirometryItem(action, index);
            } case ActionType.supervisedspirometry: {
                return supervisedSpirometryItem(action, index);
            } case ActionType.compliance: {
                return item(action, index, "Compliance", "fa-clipboard-list", complianceRules);
            } case ActionType.transmission: {
                return item(action, index, "Transmission", "fa-broadcast-tower", transmissionMethods);
            } case ActionType.spirometryinfo: {
                return spirometryInfoItem(action, index);
            }
        }
    }

    const sessionFlowDragDrop = () => {
        const onDragEnd = (result: DropResult) => {
            const onDragEndInDelete = (result: DropResult) => {
                const withoutDeleted = actions.filter(sa => sa.Id !== result.draggableId);

                setActions(withoutDeleted);
            };

            if (!result.destination) {
                return;
            }

            if (result.destination.droppableId === "Delete") {
                return onDragEndInDelete(result);
            }

            const sourceIndex = result.source.index;
            const destIndex = result.destination.index;

            const orderedSessions = ReorderActions<SessionScheduleAction>(
                actions,
                sourceIndex,
                destIndex
            );

            setActions(orderedSessions);
        };

        return (
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="SessionFlow" direction="vertical" isDropDisabled={false}>
                    {(provided, snapshot) => (
                        <>
                            <div
                                ref={provided.innerRef}
                                style={getListStyle(snapshot.isDraggingOver)}
                                onClick={e => e.stopPropagation()}
                            >
                                {actions.sort((a,b) => a.DisplayOrder - b.DisplayOrder).map((action, index) => {
                                    return <ActionItems key={`ActionItem${action.Id}`} action={action} index={index} />
                                })}
                            </div>
                            {provided.placeholder}
                        </>
                    )}
                </Droppable>
                <Droppable droppableId="Delete">
                    {(provided, snapshot) => (
                        <div
                            className="well"
                            ref={provided.innerRef}
                            style={GetBinStyle(snapshot.isDraggingOver)}
                        >
                            <i className="fas fa-lg fa-trash text-danger"></i>
                        &nbsp; Drag here to remove an item
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    };

    const onAddItem = (type: ActionType) => {
        setActions([
            ...actions,
            { Id: guid(), ActionType: type, ActionValue: "", DisplayOrder: actions.length }
        ]);
    }

    return (
        <>
            <h3 className="text-primary">Session Actions</h3>
            <div className="btn-group">
                <button
                    type="button"
                    className="btn btn-primary dropdown-toggle"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                >
                    Add Item <span className="caret"></span>
                </button>
                <ul className="dropdown-menu scrollable-menu">
                    <li>
                        <a onClick={() => onAddItem(ActionType.spirometry)}>Spirometry</a>
                    </li>
                    <li>
                        <a onClick={() => onAddItem(ActionType.unsupervisedspirometry)}>Unsupervised Spirometry</a>
                    </li>
                    <li>
                        <a onClick={() => onAddItem(ActionType.supervisedspirometry)}>Supervised Spirometry</a>
                    </li>
                    <li>
                        <a onClick={() => onAddItem(ActionType.questionnaire)}>Questionnaire</a>
                    </li>
                    <li>
                        <a onClick={() => onAddItem(ActionType.alert)}>Alert</a>
                    </li>
                    <li>
                        <a onClick={() => onAddItem(ActionType.compliance)}>Compliance</a>
                    </li>
                    <li>
                        <a onClick={() => onAddItem(ActionType.transmission)}>Transmission</a>
                    </li>
                    <li>
                        <a onClick={() => onAddItem(ActionType.spirometryinfo)}>Spirometry Info</a>
                    </li>
                </ul>
            </div>
            {sessionFlowDragDrop()}
        </>
    );
};


